import { Backdrop, Card, CardContent, CardHeader, Fade, Grid, Modal, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import WO2Button from 'src/common/components/button/Button';
import { useStyles } from 'src/themes';

interface CsvPropsBase {
  handleClose: () => void;
  isOpen: boolean;
  bulkImportOnboardingWizard: (file: File) => Promise<{ payload?: { message?: string; variant?: string } }>;
}

export const ImportOnboardingFileModal = (props: CsvPropsBase): JSX.Element => {
  const classes = useStyles();
  const theme = useTheme();
  const [acceptedFile, setAcceptedFile] = useState<File | null>();
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const [resultVariant, setResultVariant] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState<boolean>(false);

  const fileDisplay = acceptedFile && (
    <span key={acceptedFile.name}>
      File: {acceptedFile.name} - {acceptedFile.size} bytes
    </span>
  );

  const handleValidFile = async (files: File[]) => {
    const file = files[0];
    const error = file.name.indexOf('.xlsx') < 0;
    if (error) {
      setImportErrors(['Only Excel (.xlsx) documents can be uploaded.']);
      setAcceptedFile(null);
    } else {
      setImportErrors([]);
      setAcceptedFile(file);
    }
    setResultMessage(null);
    setResultVariant(null);
  };

  const completeImportCsv = async () => {
    if (acceptedFile) {
      setIsImporting(true);
      const result = await props.bulkImportOnboardingWizard(acceptedFile);
      setResultMessage(result?.payload?.message ?? null);
      setResultVariant(result?.payload?.variant ?? null);
      setIsImporting(false);
    }
  };

  useEffect(() => {
    setAcceptedFile(null);
    setImportErrors([]);
    setResultMessage(null);
    setResultVariant(null);
    setIsImporting(false);
  }, [props.isOpen]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={props.isOpen}
      onClose={() => {
        props.handleClose();
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={props.isOpen}>
        <Card
          elevation={0}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 480,
          }}
        >
          <CardHeader title={<Typography variant="h3">Import Onboarding Template (.xlsx) file</Typography>} />
          <CardContent>
            <Grid container spacing={2} direction="column">
              <Grid item>
                <Dropzone onDrop={(files) => handleValidFile(files)} multiple={false}>
                  {({ getRootProps, getInputProps }) => (
                    <div data-testid="dropzone" style={{ padding: theme.spacing(1), margin: 0 }} {...getRootProps({ className: classes.dropzone })}>
                      <input
                        {...getInputProps({
                          accept: '.xlsx',
                          type: 'file',
                        })}
                      />
                      <p>Drag and drop your file here, or click to select file</p>
                    </div>
                  )}
                </Dropzone>
              </Grid>
              {fileDisplay && (
                <Grid item>
                  <Typography sx={{ wordBreak: 'break-word' }}>{fileDisplay}</Typography>
                </Grid>
              )}
              {importErrors && importErrors.length > 0 && (
                <Grid item container direction="column" spacing={2}>
                  <Grid item maxHeight={150} overflow="auto">
                    {importErrors.map((error, index) => (
                      <Typography key={index} color="error">
                        {error}
                      </Typography>
                    ))}
                  </Grid>
                </Grid>
              )}
              {resultMessage && (
                <Grid item>
                  <Typography color={resultVariant === 'error' ? 'error' : 'textPrimary'}>
                    {resultVariant === 'error' && resultMessage.includes(':')
                      ? resultMessage.substring(0, resultMessage.indexOf(':') + 1)
                      : resultMessage}
                  </Typography>
                  {resultVariant === 'error' && resultMessage.includes(':') && (
                    <Typography
                      color="error"
                      variant="body2"
                      sx={{ maxHeight: 120, overflow: 'auto', mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {resultMessage.substring(resultMessage.indexOf(':') + 1).trim()}
                    </Typography>
                  )}
                </Grid>
              )}
              <Grid item container spacing={2} mt={2} justifyContent="space-between">
                <Grid item>
                  <WO2Button variant="outlined" onClick={props.handleClose}>
                    Close
                  </WO2Button>
                </Grid>
                {acceptedFile && !resultMessage && (
                  <Grid item>
                    <WO2Button variant="contained" onClick={completeImportCsv} disabled={isImporting}>
                      {isImporting ? 'Importing...' : 'Import'}
                    </WO2Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Fade>
    </Modal>
  );
};
